/**
 * The timer, on the page — the same object the top bar renders, at full size.
 *
 * It reads `useTimer()` and holds nothing. The number here and the number in the top
 * bar are `elapsedSecondsOf(entry, nowMs)` computed from one entry against one
 * clock, so "the page disagrees with the top bar" is not a bug that can be written
 * in this file. `web/shapes/time-tracking.md` §5.
 *
 * Three renderings that are deliberately not the same as each other:
 *
 *   loading   a skeleton. Not "no timer running" — see `MiniTimer`'s header.
 *   error     the read failed, with a retry. Not "no timer running" either.
 *   ready     the entry, or an empty state that says how to start one.
 *
 * The long-running note is an **elapsed fact**, not a policy claim: see
 * `LONG_RUNNING_SECONDS` in `../../timer/elapsed.ts` for why the client cannot know
 * a tenant's `timer_auto_stop_minutes` and must not imply one.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Icon } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { ValidationBadge, validationReasonText } from '../../timer/badges';
import { formatClock, LONG_RUNNING_SECONDS } from '../../timer/elapsed';
import { useTimer } from '../../timer/TimerProvider';

export function TimerPanel({ timezone }: { readonly timezone: string }): ReactNode {
  const { t, locale } = useI18n();
  const {
    status,
    entry,
    error,
    busy,
    actionError,
    switchedFrom,
    elapsedSeconds,
    pause,
    resume,
    stop,
    reload,
  } = useTimer();

  if (status === 'loading') {
    return (
      <section className="wp-section" aria-labelledby="timer-heading">
        <h2 className="wp-section__title" id="timer-heading">
          {t('timer.heading')}
        </h2>
        <Skeleton rows={2} variant="block" label={t('timer.loading')} testId="timer-skeleton" />
      </section>
    );
  }

  return (
    <section className="wp-section" aria-labelledby="timer-heading">
      <h2 className="wp-section__title" id="timer-heading">
        {t('timer.heading')}
      </h2>

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('timer.subject')}
          testId="timer-read-error"
        />
      ) : null}

      {actionError !== null ? (
        <ApiErrorNote
          error={actionError}
          subject={t('timer.subject.action')}
          testId="timer-action-error"
        />
      ) : null}

      {switchedFrom !== null ? (
        <StatusNote severity="info" title={t('timer.switched.title')} testId="timer-switched-from">
          <p>
            {t('timer.switched.body', {
              task:
                switchedFrom.task_title ?? switchedFrom.achau_task_id ?? t('timer.task.unnamed'),
            })}
          </p>
        </StatusNote>
      ) : null}

      {status === 'ready' && entry === null ? (
        <EmptyState
          title={t('timer.empty.title')}
          description={t('timer.empty.body')}
          testId="timer-empty"
        />
      ) : null}

      {entry === null ? null : (
        <div className="wp-card wp-timer-panel" data-testid="timer-panel">
          <p className="wp-timer-panel__task">
            {entry.task_title ?? entry.achau_task_id ?? t('timer.task.unnamed')}
          </p>
          <p className="wp-timer-panel__clock wp-mono" role="timer" aria-live="off">
            <span className="wp-visually-hidden">{t('timer.elapsed')}: </span>
            {formatClock(elapsedSeconds)}
          </p>
          <p className="wp-small wp-muted">
            {t('timer.startedAt', { when: formatInstant(entry.started_at, locale, timezone) })}
          </p>

          <div className="wp-timer-panel__controls">
            <button
              type="button"
              className="wp-button wp-button--primary"
              disabled={busy}
              onClick={() => {
                void (entry.status === 'running' ? pause() : resume());
              }}
              data-testid="timer-toggle"
            >
              <Icon name={entry.status === 'running' ? 'pause' : 'play'} />
              {entry.status === 'running' ? t('timer.pause') : t('timer.resume')}
            </button>
            <button
              type="button"
              className="wp-button"
              disabled={busy}
              onClick={() => {
                void stop();
              }}
              data-testid="timer-stop"
            >
              <Icon name="square" />
              {t('timer.stop')}
            </button>
          </div>

          <p className="wp-flags">
            <ValidationBadge state={entry.task_validation_state} testId="timer-validation" />
          </p>

          {/* The whole sentence, not just the badge. `pending_validation` is the one
              state on this screen most likely to be read as an accusation, so the
              body copy says what it means: the time is recorded, nothing is missing,
              and the outstanding question is about achau. */}
          {entry.task_validation_state === 'pending_validation' ? (
            <StatusNote
              severity="info"
              title={t('timer.validation.pending')}
              testId="timer-pending-validation"
            >
              <p>{t('timer.validation.pending.body')}</p>
              <p className="wp-small wp-muted">
                {validationReasonText(entry.task_validation_reason, t) ??
                  t('timer.reason.achau_unreachable')}
              </p>
              <p className="wp-small wp-muted">{t('timer.validation.pending.checkedAt')}</p>
            </StatusNote>
          ) : null}

          {entry.task_validation_state === 'validation_failed' ? (
            <StatusNote
              severity="medium"
              title={t('timer.validation.failed')}
              testId="timer-failed-validation"
            >
              <p>{t('timer.validation.failed.body')}</p>
              <p className="wp-small wp-muted">
                {validationReasonText(entry.task_validation_reason, t) ?? t('timer.reason.unknown')}
              </p>
            </StatusNote>
          ) : null}

          {entry.status === 'running' && elapsedSeconds >= LONG_RUNNING_SECONDS ? (
            <StatusNote
              severity="info"
              title={t('timer.longRunning.title')}
              testId="timer-long-running"
            >
              <p>
                {t('timer.longRunning.body', { hours: Math.floor(LONG_RUNNING_SECONDS / 3600) })}
              </p>
            </StatusNote>
          ) : null}
        </div>
      )}
    </section>
  );
}
